// Inscription en deux écrans (D-015) : identité et mot de passe, puis code à six chiffres reçu par
// SMS ou courriel (POST /public/inscription, /inscription/confirmer). Le compte n'existe qu'après
// la confirmation ; la connexion est ouverte aussitôt avec le mot de passe saisi.
import { schemaNouveauMotDePasse, type SessionOuverte } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Formulaire } from '@cc/ui';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { requetePublique } from '../../../api/client';
import { ErreurApi, messageErreur } from '../../../api/erreurs';
import { useCollectiviteChoisie } from '../collectivite';
import { EnTeteCitoyen } from '../commun';
import { useSessionUsager } from '../session/FournisseurSessionUsager';
import styles from '../citoyen.module.css';

type Canal = 'sms' | 'courriel';
type ErreurChamp = { champ: string; message: string };

interface ReponseInscription {
  message: string;
  identifiant: string;
  canal: Canal;
  expire_dans_minutes: number;
}

/** Erreurs de l'API par champ, ramenées aux identifiants des champs de l'écran. */
function erreursApi(e: unknown, prefixe: string): ErreurChamp[] {
  if (!(e instanceof ErreurApi)) return [];
  return e.champs.map((d) => ({ champ: `${prefixe}-${d.champ.replace(/_/g, '-')}`, message: d.message }));
}

export function PageInscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const { ouvrir } = useSessionUsager();
  const [choisie] = useCollectiviteChoisie();
  const destination = (location.state as { depuis?: string } | null)?.depuis ?? (choisie ? `/citoyen/${choisie.code}` : '/citoyen');
  const [etape, setEtape] = useState<1 | 2>(1);
  const [nom, setNom] = useState('');
  const [prenoms, setPrenoms] = useState('');
  const [telephone, setTelephone] = useState('');
  const [courriel, setCourriel] = useState('');
  const [canal, setCanal] = useState<Canal>('sms');
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [envoi, setEnvoi] = useState<ReponseInscription | null>(null);
  const [erreurs, setErreurs] = useState<ErreurChamp[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const erreurChamp = (champ: string) => erreurs.find((e) => e.champ === champ)?.message ?? null;

  const demanderCode = async () => {
    const locales: ErreurChamp[] = [];
    if (!nom.trim()) locales.push({ champ: 'inscription-nom', message: 'Le nom est obligatoire' });
    if (!telephone.trim() && !courriel.trim()) locales.push({ champ: 'inscription-telephone', message: 'Indiquez un téléphone ou un courriel' });
    if (canal === 'sms' && !telephone.trim()) locales.push({ champ: 'inscription-canal', message: 'Le code par SMS exige un téléphone' });
    if (canal === 'courriel' && !courriel.trim()) locales.push({ champ: 'inscription-canal', message: 'Le code par courriel exige un courriel' });
    const mdp = schemaNouveauMotDePasse.safeParse(motDePasse);
    if (!mdp.success) locales.push({ champ: 'inscription-mot-de-passe', message: mdp.error.issues[0]?.message ?? 'Mot de passe trop faible' });
    else if (confirmation !== motDePasse) locales.push({ champ: 'inscription-confirmation', message: 'La confirmation ne correspond pas au mot de passe' });
    setErreurs(locales);
    if (locales.length > 0) return;
    setErreur(null);
    setEnCours(true);
    try {
      const r = await requetePublique<ReponseInscription>('/public/inscription', {
        ...(telephone.trim() ? { telephone: telephone.trim() } : {}),
        ...(courriel.trim() ? { courriel: courriel.trim() } : {}),
        canal,
        mot_de_passe: motDePasse,
        nom: nom.trim(),
        ...(prenoms.trim() ? { prenoms: prenoms.trim() } : {}),
      });
      setEnvoi(r);
      setEtape(2);
    } catch (e) {
      const parChamp = erreursApi(e, 'inscription');
      setErreurs(parChamp);
      if (parChamp.length === 0) setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  const confirmer = async () => {
    const c = code.replace(/\s/g, '');
    if (!/^\d{6}$/.test(c)) {
      setErreurs([{ champ: 'inscription-code', message: 'Le code comporte six chiffres' }]);
      return;
    }
    if (!envoi) return;
    setErreurs([]);
    setErreur(null);
    setEnCours(true);
    try {
      await requetePublique('/public/inscription/confirmer', { identifiant: envoi.identifiant, code: c });
      const session = await requetePublique<SessionOuverte>('/public/connexion', { identifiant: envoi.identifiant, mot_de_passe: motDePasse });
      ouvrir(session);
      navigate(destination, { replace: true });
    } catch (e) {
      if (e instanceof ErreurApi && (e.code === 'code_invalide' || e.statut === 400)) setErreur('Code incorrect ou expiré. Vérifiez le message reçu ou demandez un nouveau code.');
      else if (e instanceof ErreurApi && e.code === 'compte_existant') setErreur('Un compte existe déjà pour cet identifiant : connectez-vous ou utilisez « Mot de passe oublié ».');
      else setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <>
      <EnTeteCitoyen
        titre={etape === 1 ? 'Créer un compte' : 'Code reçu'}
        surTitre={`Étape ${etape} sur 2`}
        sousTitre={etape === 1 ? 'Un seul compte pour toutes les collectivités. Le téléphone est l\'identifiant principal.' : undefined}
        retour={etape === 1 ? '/citoyen/connexion' : undefined}
        libelleRetour="Connexion"
      />
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      {etape === 1 ? (
        <Formulaire
          libelle="Inscription, étape 1"
          onSoumettre={() => void demanderCode()}
          erreurs={erreurs}
          actions={
            <Bouton type="submit" chargement={enCours} libelleChargement="Envoi du code en cours" pleineLargeur>
              Recevoir le code
            </Bouton>
          }
        >
          <Champ id="inscription-nom" libelle="Nom" autoComplete="family-name" value={nom} onChange={(e) => setNom(e.target.value)} erreur={erreurChamp('inscription-nom')} required />
          <Champ id="inscription-prenoms" libelle="Prénoms" autoComplete="given-name" value={prenoms} onChange={(e) => setPrenoms(e.target.value)} facultatif />
          <Champ
            id="inscription-telephone"
            libelle="Téléphone"
            type="tel"
            autoComplete="tel"
            aide="Numéro gabonais sans indicatif, ou numéro international commençant par +"
            value={telephone}
            onChange={(e) => setTelephone(e.target.value)}
            erreur={erreurChamp('inscription-telephone')}
          />
          <Champ id="inscription-courriel" libelle="Courriel" type="email" autoComplete="email" value={courriel} onChange={(e) => setCourriel(e.target.value)} erreur={erreurChamp('inscription-courriel')} facultatif />
          <fieldset className={styles.pile} id="inscription-canal" aria-describedby={erreurChamp('inscription-canal') ? 'inscription-canal-erreur' : undefined}>
            <legend>Recevoir le code par</legend>
            <div className={styles.ligneBoutons}>
              <label>
                <input type="radio" name="canal" value="sms" checked={canal === 'sms'} onChange={() => setCanal('sms')} /> SMS
              </label>
              <label>
                <input type="radio" name="canal" value="courriel" checked={canal === 'courriel'} onChange={() => setCanal('courriel')} /> Courriel
              </label>
            </div>
            {erreurChamp('inscription-canal') ? (
              <p id="inscription-canal-erreur" className={styles.statutAttention}>
                {erreurChamp('inscription-canal')}
              </p>
            ) : null}
          </fieldset>
          <Champ
            id="inscription-mot-de-passe"
            libelle="Mot de passe"
            type="password"
            autoComplete="new-password"
            aide="Douze caractères au moins, avec une lettre et un chiffre"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            erreur={erreurChamp('inscription-mot-de-passe')}
            required
          />
          <Champ
            id="inscription-confirmation"
            libelle="Confirmer le mot de passe"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            erreur={erreurChamp('inscription-confirmation')}
            required
          />
        </Formulaire>
      ) : (
        <Formulaire
          libelle="Inscription, étape 2"
          onSoumettre={() => void confirmer()}
          erreurs={erreurs}
          actions={
            <>
              <Bouton type="submit" chargement={enCours} libelleChargement="Vérification en cours" pleineLargeur>
                Confirmer et ouvrir mon compte
              </Bouton>
              <Bouton variante="discret" disabled={enCours} onClick={() => void demanderCode()}>
                Renvoyer le code
              </Bouton>
            </>
          }
        >
          <p>
            Si ces informations sont valides, un code à six chiffres a été envoyé par {envoi?.canal === 'sms' ? 'SMS' : 'courriel'} à{' '}
            <span className={styles.mono}>{envoi?.identifiant}</span>. Il reste valable {envoi?.expire_dans_minutes ?? 15} minutes.
          </p>
          <Champ
            id="inscription-code"
            libelle="Code à six chiffres"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            erreur={erreurChamp('inscription-code')}
            required
          />
          <p className={styles.aide}>
            Déjà un compte avec cet identifiant ? Aucun code n&apos;est envoyé : <Link to="/citoyen/connexion">connectez-vous</Link>.
          </p>
        </Formulaire>
      )}
    </>
  );
}
